import React, { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Landing.css';

function Landing() {
    const navigate = useNavigate();
    const { currentUser } = useAuth();

    // Redirect if already logged in
    useEffect(() => {
        if (currentUser) {
            navigate('/home');
        }
    }, [currentUser, navigate]);

    const handleGetStarted = () => {
        navigate('/auth');
    };

    const features = [
        {
            icon: '🧠',
            title: 'AI-Powered Learning Paths',
            description: 'Get a personalized curriculum for any topic, from NCLEX to Golf. Our AI creates structured learning paths tailored just for you.'
        },
        {
            icon: '🎮',
            title: 'Gamified Experience',
            description: 'Earn XP, level up, maintain streaks, and unlock achievements. Learning should feel like playing, not studying.'
        },
        {
            icon: '🔄',
            title: 'Spaced Repetition',
            description: 'Our SM-2 algorithm ensures you never forget. Cards are scheduled at optimal intervals for maximum retention.'
        },
        {
            icon: '⚡',
            title: 'Interactive Lessons',
            description: 'Bite-sized, engaging content with quizzes, flashcards, and real-world examples. Learn by doing, not just reading.'
        },
        {
            icon: '📊',
            title: 'Progress Tracking',
            description: 'Visualize your learning journey with detailed analytics, completion rates, and skill assessments.'
        },
        {
            icon: '🎯',
            title: 'Adaptive Difficulty',
            description: 'Content automatically adjusts to your skill level. Always challenged, never overwhelmed.'
        }
    ];

    const useCases = [
        { emoji: '🏥', title: 'NCLEX Prep', subtitle: 'Nursing students' },
        { emoji: '✈️', title: 'FAA Part 107', subtitle: 'Drone pilots' },
        { emoji: '🏡', title: 'Real Estate License', subtitle: 'Realtors' },
        { emoji: '⛳', title: 'Golf Mastery', subtitle: 'Golf enthusiasts' },
        { emoji: '🗣️', title: 'Language Learning', subtitle: 'Polyglots' },
        { emoji: '💻', title: 'Tech Certifications', subtitle: 'Developers' },
        { emoji: '📚', title: 'Academic Subjects', subtitle: 'Students' },
        { emoji: '🎨', title: 'Creative Skills', subtitle: 'Artists' }
    ];

    return (
        <div className="landing-page">
            {/* Hero Section */}
            <section className="hero-section">
                <div className="hero-content">
                    <div className="hero-badge">🚀 AI-Powered Learning Platform</div>
                    <h1 className="hero-title">
                        Learn <span className="gradient-text">Anything</span>,
                        <br />
                        Master <span className="gradient-text">Everything</span>
                    </h1>
                    <p className="hero-subtitle">
                        Your personal AI tutor for any subject. Pass the NCLEX, master Golf, learn Spanish,
                        or ace any certification. Powered by spaced repetition and adaptive learning.
                    </p>
                    <div className="hero-cta">
                        <button className="btn btn-primary btn-hero" onClick={handleGetStarted}>
                            Start Learning Free →
                        </button>
                        <button className="btn btn-secondary btn-hero" onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}>
                            See How It Works
                        </button>
                    </div>
                </div>
            </section>

            {/* Use Cases Grid */}
            <section className="use-cases-section">
                <h2 className="section-title">Learn Absolutely Anything</h2>
                <p className="section-subtitle">
                    From professional certifications to personal hobbies - we've got you covered
                </p>
                <div className="use-cases-grid">
                    {useCases.map((useCase, index) => (
                        <div key={index} className="use-case-card" style={{ animationDelay: `${index * 0.1}s` }}>
                            <div className="use-case-emoji">{useCase.emoji}</div>
                            <h3>{useCase.title}</h3>
                            <p>{useCase.subtitle}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Features Section */}
            <section id="features" className="features-section">
                <h2 className="section-title">Why Choose Our Platform?</h2>
                <p className="section-subtitle">
                    The most advanced learning technology, simplified
                </p>
                <div className="features-grid">
                    {features.map((feature, index) => (
                        <div key={index} className="feature-card-landing">
                            <div className="feature-icon-large">{feature.icon}</div>
                            <h3>{feature.title}</h3>
                            <p>{feature.description}</p>
                        </div>
                    ))}
                </div>
            </section>

            {/* How It Works */}
            <section className="how-it-works-section">
                <h2 className="section-title">How It Works</h2>
                <div className="steps-container">
                    <div className="step">
                        <div className="step-number">1</div>
                        <div className="step-content">
                            <h3>Choose Your Topic</h3>
                            <p>Type in anything you want to learn - NCLEX, Golf, Spanish, anything!</p>
                        </div>
                    </div>
                    <div className="step-arrow">→</div>
                    <div className="step">
                        <div className="step-number">2</div>
                        <div className="step-content">
                            <h3>AI Creates Your Path</h3>
                            <p>Our AI generates a personalized learning journey with structured modules</p>
                        </div>
                    </div>
                    <div className="step-arrow">→</div>
                    <div className="step">
                        <div className="step-number">3</div>
                        <div className="step-content">
                            <h3>Learn & Master</h3>
                            <p>Interactive lessons, smart flashcards, and quizzes - track your progress!</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Social Proof */}
            <section className="testimonials-section">
                <h2 className="section-title">Join Thousands of Learners</h2>
                <div className="testimonials-grid">
                    <div className="testimonial-card">
                        <div className="testimonial-stars">⭐⭐⭐⭐⭐</div>
                        <p className="testimonial-text">
                            "Passed my NCLEX on the first try! The spaced repetition feature is a game-changer."
                        </p>
                        <div className="testimonial-author">
                            <strong>Sarah M.</strong>
                            <span>Nursing Student</span>
                        </div>
                    </div>
                    <div className="testimonial-card">
                        <div className="testimonial-stars">⭐⭐⭐⭐⭐</div>
                        <p className="testimonial-text">
                            "Finally got my FAA Part 107 license! This app made studying actually enjoyable."
                        </p>
                        <div className="testimonial-author">
                            <strong>Mike T.</strong>
                            <span>Drone Pilot</span>
                        </div>
                    </div>
                    <div className="testimonial-card">
                        <div className="testimonial-stars">⭐⭐⭐⭐⭐</div>
                        <p className="testimonial-text">
                            "I use it for everything - from coding to cooking. It's like Duolingo for life!"
                        </p>
                        <div className="testimonial-author">
                            <strong>Alex R.</strong>
                            <span>Software Engineer</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Final CTA */}
            <section className="final-cta-section">
                <div className="final-cta-content">
                    <h2>Ready to Transform Your Learning?</h2>
                    <p>Join thousands mastering new skills every day. Start for free, no credit card required.</p>
                    <button className="btn btn-primary btn-hero" onClick={handleGetStarted}>
                        Get Started Now - It's Free! →
                    </button>
                    <p className="cta-subtext">✓ No credit card required  ✓ Unlimited topics  ✓ Cancel anytime</p>
                </div>
            </section>

            {/* Footer */}
            <footer className="landing-footer">
                <div className="footer-content">
                    <div className="footer-section">
                        <h3>🧠 AI Study Guide</h3>
                        <p>The future of learning, today.</p>
                    </div>
                    <div className="footer-section">
                        <h4>Product</h4>
                        <a href="#features">Features</a>
                        <a href="#pricing">Pricing</a>
                        <a href="#testimonials">Testimonials</a>
                    </div>
                    <div className="footer-section">
                        <h4>Legal</h4>
                        <Link to="/legal/privacy">Privacy Policy</Link>
                        <Link to="/legal/terms">Terms of Service</Link>
                        <a href="mailto:support@learnviva.com">Contact</a>
                    </div>
                </div>
                <div className="footer-bottom">
                    <p>&copy; 2025 LearnViva. Built with 💜 for learners everywhere.</p>
                </div>
            </footer>
        </div>
    );
}

export default Landing;
